// React
import { useCallback, useEffect, useState } from 'react';

// Mobx
import { observer } from 'mobx-react';

// Interfaces
import { IBackOfficeUserDetailCardProps } from './interfaces';
import { IRoute } from 'routes/route.interface';
import { TUserDetailDialog, USER_DETAIL_DIALOG_TYPE, TUserComponentsDialogType } from './interfaces';

// Components
import { UserDetailHeader, ChangeRoleDialog } from './components';
import { BaseImage, BaseDialogVerification } from 'components';
import { UserApproveRegistrationDialog, UserRejectRegistrationDialog } from 'pages/back-office/user/components';

// Antd
import { Menu, Button } from 'antd';

// React Router Dom
import { useHistory, useLocation, useParams } from 'react-router-dom';

// Models
import { useStores } from 'models';

// Custom Hook
import { useBackOfficeRoutes, useUser, useRequestSaver, useMasterRole, useAuth } from 'hooks';

// i18n
import { useTranslation } from 'react-i18next';

// Styles
import './styles.scss'

// Constant
import { REQUEST } from 'constant/request.constant';
import { USER_STATUS } from 'constant/shared.constant';
import { USER_URL } from 'constant/url.constant';

// Utils
import { messageUtils_success, messageUtils_error } from 'utils/common/message.utils';

const BackOfficeUserDetailCard = observer(({children, meta}: IBackOfficeUserDetailCardProps) => {
  const { t } = useTranslation();
  const params = useParams<{userId: string}>();
  const history = useHistory();
  const [detailDialogEditableType, setDetailDialogEditableType] = useState<TUserDetailDialog>('')
  const [dialog, setDialog] = useState<{
    isBulkRegisterDialogOpen: boolean
    isDetailDialogOpen: boolean
    isApproveDialogOpen: boolean
    isRejectDialogOpen: boolean
    isChangeRoleDialogOpen: boolean
    isConfirmationDialogOpen: boolean
  }>({
    isBulkRegisterDialogOpen: false,
    isDetailDialogOpen: false,
    isApproveDialogOpen: false,
    isRejectDialogOpen: false,
    isChangeRoleDialogOpen: false,
    isConfirmationDialogOpen: false,
  });
  const { 
    masterRole_loading, 
    masterRole_dropdown, 
    masterRole_fetchDropdown,
    masterRole_CLEAR_DROPDOWN 
  } = useMasterRole()
  const {
    user_loading,
    user_detail,
    user_rejectForm,
    user_approveForm,
    user_SET_REJECT_FORM,
    user_CLEAR_REJECT_FORM,
    user_SET_APPROVE_FORM,
    user_CLEAR_APPROVE_FORM,
    user_CLEAR_DETAIL,
    user_fetchDetail,
    user_approve,
    user_reject,
    user_delete,
    user_update
  } = useUser()
  const { requestSaver_setCancelToken, requestSaver_cancel } = useRequestSaver();
  const location = useLocation();
  const {
    auth: { auth_authenticatedUser }
  } = useStores();
  const { childrenRoutes } = useBackOfficeRoutes({ authenticatedUser: auth_authenticatedUser, module: meta.module, optionalModule: meta.attrs?.optionalModule })
  const { auth_persisted, auth_getAuthenticatedUser } = useAuth()

  useEffect(() => {
    getUserDetail(params.userId);

    // eslint-disable-next-line
  }, [children, meta, params.userId])

  // Clear state from current component
  useEffect(() => {
    return () => {
      onClear();
    };

    // eslint-disable-next-line
  }, [])

  /**
   * @description Clear additional data
   *
   * @return {void} void
   */
  const onClearAdditionalData = useCallback((): void => {
    masterRole_CLEAR_DROPDOWN()
    user_CLEAR_REJECT_FORM()
    user_CLEAR_APPROVE_FORM()
    setDetailDialogEditableType('')

    // eslint-disable-next-line
  }, [])

  /**
   * @description Clear state from current component
   *
   * @return {void} void
   */
  const onClear = useCallback((): void => {
    user_CLEAR_DETAIL()

    // Clear Addtional Data
    onClearAdditionalData()

    // eslint-disable-next-line
  }, []);

  /**
   * @description Get single user
   *
   * @param {number} id
   *
   * @return {Promise<void>} Promise<void>
   */
  const getUserDetail = useCallback(
    (id: string): Promise<void> => {
      return user_fetchDetail({
        params: { id },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_FETCH_DETAIL_REQUEST) },
      })
    },
    [user_fetchDetail, requestSaver_setCancelToken],
  )

  /**
   * @description filter route: if a user isn't a corcomm then just return two route (personal and company data) otherwise return all
   *
   * @return {IRoute[]} routes
   */
  const filterRoute = useCallback((): IRoute[] => {
    // check if not corcomm
    if (user_detail.fieldId !== 2){
      return childrenRoutes.filter((route: IRoute) => !route.path.includes('my-document') && !route.path.includes('additional-question'))
    }
    return childrenRoutes
  }, [childrenRoutes, user_detail.fieldId])

  /**
   * @description convert user status to url
   *
   * @return {string} url
   */
  const renderUserStatus = useCallback((): string => {
    if (user_detail.status === USER_STATUS.WAITING_APPROVAL){
      return USER_URL.WAITING_APPROVAL;
    } else if (user_detail.status === USER_STATUS.APPROVED){
      return USER_URL.APPROVED;
    } else {
      return USER_URL.REJECTED;
    }
  }, [user_detail])

  /**
   * @description Redirect Back
   *
   * @return {void} void
   */
  const onRedirectBack = useCallback((): void => {
    history.replace(renderUserStatus());

    // eslint-disable-next-line
  }, [renderUserStatus])

    /**
   * @description Get role dropdown
   *
   * @return {Promise<void>} Promise<void>
   */
  const getRoleDropdown = useCallback((): Promise<void> => {
    return masterRole_fetchDropdown({
      requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_MASTER_DATA_ROLE_FETCH_DROPDOWN_REQUEST) },
    })

    // eslint-disable-next-line
  }, [])

    /**
   * @description Handle dialog
   *
   * @param {TUserComponentsDialogType} dialogType
   * @param {boolean} value
   * @param {object} options
   *
   * @return {Promise<void>} Promise<void>
   */
    const handleDialog = useCallback(
      async (
        dialogType: TUserComponentsDialogType,
        value: boolean,
        options?: { editableDialogType?: TUserDetailDialog; userId?: string },
      ): Promise<void> => {
        setDialog(dialog => ({
          ...dialog,
          [dialogType]: value,
        }))
  
        // Load user, otherwise, close the modal
        try {
          if (value) {
            // Check for user ID
            if (options?.userId) await getUserDetail(options.userId)
  
            // Check for editable dialog type
            if (options?.editableDialogType) setDetailDialogEditableType(options.editableDialogType)
  
            // Check if opening approval
            if (dialogType === 'isApproveDialogOpen' || dialogType === 'isChangeRoleDialogOpen') await getRoleDropdown()
          }
        } catch (err) {
          handleDialog(dialogType, false)
        }
  
        if (!value) {
          // Don't clear state when one of this dialogs opened
          const whitelistedDialogs = ['isRejectDialogOpen', 'isApproveDialogOpen', 'isChangeRoleDialogOpen'].includes(dialogType)
  
          if (!whitelistedDialogs) {
            // Clear additional data
            onClearAdditionalData()
          }
  
          // Cancel request if theres any request in the background
          requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_FETCH_DETAIL_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_ROLE_FETCH_DROPDOWN_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_CREATE_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_APPROVE_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REJECT_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REMOVE_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_UPDATE_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DROPDOWN_REQUEST)
          requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DETAIL_REQUEST)
        }
      },
      [getRoleDropdown, getUserDetail, requestSaver_cancel, onClearAdditionalData],
    )

   /**
   * @description Handle user detail dialog click approve button
   *
   * @return {void} void
   */
   const handleUserDetailClickApproveButton = useCallback((): void => {
    
    handleDialog('isApproveDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.APPROVE_USER })
  
  }, [handleDialog])

  /**
   * @description Handle user detail dialog click reject button
   *
   * @param {IUserUpdateForm} form
   *
   * @return {void} void
   */
  const handleUserDetailClickRejectButton = useCallback((): void => {

    handleDialog('isRejectDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.REJECT_USER })
    
  }, [handleDialog])

  /**
   * @description Handle user detail click activate / deactivate button
   *
   * @return {void} void
   */
  const handleUserDetailClickChangeAccountStatus = useCallback((): void => {
    
    handleDialog('isConfirmationDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS })
  
  }, [handleDialog])

  /**
   * @description Handle user detail change role dialog
   *
   * @return {void} void
   */
  const handleUserDetailClickChangeRole = useCallback((): void => {
    
    handleDialog('isChangeRoleDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.CHANGE_USER_ROLE })
  
  }, [handleDialog])

  /**
   * @description Open delete confirmation dialog
   *
   * @return {void} void
   */
  const handleUserDetailClickDelete = useCallback((): void => {
    
    handleDialog('isConfirmationDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.REMOVE_USER })
  
  }, [handleDialog])

   /**
   * @description Handle user approve dialog click first button
   *
   * @param {{roleIds: number[]}} form
   *
   * @return {void} void
   */
   const handleUserApproveClickFirstButton = useCallback(
    (form: { roleIds: number[] }): void => {
      handleDialog('isApproveDialogOpen', false)

      // Set form globally
      user_SET_APPROVE_FORM(form)

      handleDialog('isConfirmationDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.APPROVE_USER })
    },
    [handleDialog, user_SET_APPROVE_FORM],
  )

  /**
   * @description Handle user reject dialog click first button
   *
   * @param {{reason: string}} form
   *
   * @return {void} void
   */
  const handleUserRejectClickFirstButton = useCallback(
    (form: { reason: string }): void => {
      handleDialog('isRejectDialogOpen', false)

      // Set form globally
      user_SET_REJECT_FORM(form)

      handleDialog('isConfirmationDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.REJECT_USER })
    },
    [handleDialog, user_SET_REJECT_FORM],
  )

    /**
   * @description Approve user
   *
   * @param {number} id
   * @param {{roleIds: number[]}} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const approveUser = useCallback(
    (id: number, form: { roleIds: number[] }): Promise<void> => {
      return user_approve({
        params: { id },
        body: { roleIds: form.roleIds },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_APPROVE_REQUEST) },
      })
    },
    [user_approve, requestSaver_setCancelToken],
  )

  /**
   * @description Reject user
   *
   * @param {number} id
   * @param {{reason: string}} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const rejectUser = useCallback(
    (id: number, form: { reason: string }): Promise<void> => {
      return user_reject({
        params: { id },
        body: { reason: form.reason },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REJECT_REQUEST) },
      })
    },
    [user_reject, requestSaver_setCancelToken],
  )

  /**
   * @description Remove user
   *
   * @param {number} id
   *
   * @return {Promise<void>} Promise<void>
   */
  const deleteUser = useCallback(
    (id: number): Promise<void> => {
      return user_delete({
        params: { id },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REMOVE_REQUEST) },
      })
    },
    [user_delete, requestSaver_setCancelToken],
  )

  /**
   * @description Change Account Status 
   *
   * @param {number} userId
   * @param {IUserUpdateForm} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const changeAccountStatus = useCallback(
    (userId: number, accountStatus: boolean): Promise<void> => {
      return user_update({
        params: { id: userId },
        body: { 
          name: user_detail.name,
          email: user_detail.email,
          npk: user_detail.npk,
          companyId: user_detail.companyId,
          fieldId: user_detail.fieldId,
          roleIds: user_detail.roles.map(role => role.id!),
          accountStatus
        },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_UPDATE_REQUEST) },
      })
    },
    [requestSaver_setCancelToken, user_update, user_detail],
  )

  /**
   * @description Change User Role 
   *
   * @param {number[]} roleIds
   *
   * @return {Promise<void>} Promise<void>
   */
  const changeUserRole = useCallback(
    async (form: { roleIds: number[] }): Promise<void> => {
      handleDialog('isChangeRoleDialogOpen', false)

      // Update user
      try {
        await user_update({
          params: { id: user_detail.id! },
          body: { 
            name: user_detail.name,
            email: user_detail.email,
            npk: user_detail.npk,
            companyId: user_detail.companyId,
            fieldId: user_detail.fieldId,
            roleIds: form.roleIds,
            accountStatus: user_detail.accountStatus
          },
          requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_UPDATE_REQUEST) },
        })
        messageUtils_success(t('user.update.alert.success'))
        
        // Show updated data
        getUserDetail(params.userId)
      } catch (__) {
        // 
      }
    },
    [
      params.userId, 
      user_detail, 
      handleDialog, 
      getUserDetail, 
      requestSaver_setCancelToken, 
      t,
      user_update
    ]
  )

  /**
   * @description Handle approve / reject confirmation dialog click first button
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleVerificationClickFirstButton = useCallback(async (): Promise<void> => {
    // Check if user_detail.id is empty
    if (!user_detail?.id) {
      handleDialog('isConfirmationDialogOpen', false)

      messageUtils_error(t(`user.alert.userIdEmpty`))

      return
    }

    const locale =
      detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.APPROVE_USER
        ? 'approve'
        : detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REJECT_USER
        ? 'reject'
        : 'remove'

    try {
      await (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.APPROVE_USER
        ? approveUser(user_detail.id, { roleIds: user_approveForm.roleIds })
        : detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REJECT_USER
        ? rejectUser(user_detail.id, { reason: user_rejectForm.reason })
        : detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REMOVE_USER
        ? deleteUser(user_detail.id)
        : changeAccountStatus(user_detail.id, !user_detail.accountStatus))
      // Close dialog when success
      handleDialog('isConfirmationDialogOpen', false)

      // Throw success alert
      if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS) {
        messageUtils_success(t('user.update.alert.success'))

        // Show updated data
        getUserDetail(params.userId)
      }
      else {
        messageUtils_success(t(`user.${locale}.alert.success`))

        // Redirect to user management
        onRedirectBack()
      }
      
      // Check if user updating the same user as the logged in
      if (user_detail.id === auth_persisted?.auth_authenticatedUser?.id) {
        await auth_getAuthenticatedUser()
      }

    } catch (err) {
      //
    }
  }, [
    detailDialogEditableType,
    user_detail?.id,
    user_approveForm.roleIds,
    user_rejectForm.reason,
    t,
    approveUser,
    deleteUser,
    handleDialog,
    rejectUser,
    onRedirectBack,
    getUserDetail,
    changeAccountStatus,
    params.userId,
    user_detail.accountStatus
  ])

  /**
   * @description Render dialog verification body
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderDialogVerificationBody = useCallback((): JSX.Element => {
    let element

    // Check if user want to approve
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.APPROVE_USER) {
      element = <p className="text-grey-font text-16">{t('user.approve.confirm')}</p>
    }

    // Check if status is waiting approval
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REJECT_USER) {
      element = <p className="text-grey-font text-16">{t('user.reject.confirm')}</p>
    }

    // Check if status is rejected
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REMOVE_USER) {
      element = <p className="text-grey-font text-16">{t('user.remove.confirm')}</p>
    }

    // Check if status is change account status
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS) {
      element = <p className="text-grey-font text-16">{ user_detail.accountStatus ? t('user.update.confirm.deActivate') : t('user.update.confirm.activate')}</p>
    }

    return (
      <div className="flex flex-col items-center justify-center text-center my-8" style={{ width: '100%' }}>
        {element}
        <strong>{user_detail?.name ? `${user_detail?.name}?` : '-'}</strong>
      </div>
    )
  }, [detailDialogEditableType, user_detail.name, t, user_detail.accountStatus])

  /**
   * @description Render dialog verification footer for change account status
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderDialogVerificationFooter = useCallback((): JSX.Element => {
    if (detailDialogEditableType !== USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS) return <></>
    return <>
      <Button 
        type="primary" 
        className="custom-button" 
        onClick={handleVerificationClickFirstButton} 
        loading={user_loading.isDetailLoading || user_loading.isActionLoading}
        style={{ backgroundColor: user_detail.accountStatus ? '#FF5858' : '#0E38B7' }}
        >
        {t('app.sure')}
      </Button>

      <Button 
        type="text" 
        className="custom-button" 
        onClick={() => handleDialog('isConfirmationDialogOpen', false)} 
        style={{ backgroundColor: user_detail.accountStatus ? '#0E38B7' : '#FF5858' }}
        >
        {t('app.no')}
      </Button>
    </>
  }, [detailDialogEditableType, user_loading, user_detail.accountStatus, t, handleVerificationClickFirstButton, handleDialog])

  return <>
    {/* Back to user management */}
    <div onClick={onRedirectBack} className="cursor-pointer flex flex-row space-x-2 mb-3">
      <div className='p-1'>
        <BaseImage isIcon isSvg icon={'icon-chevron-left-blue'} height='10px'/>
      </div>
      <p className="text-primary font-bold text-xs leading-4">{t('user.action.backToUserManagement')}</p>
    </div>

    {/* Header */}
    <div className='back-office-user-detail-card-header'>
      <UserDetailHeader
        title={`Detail User ${user_detail.status}`}
        name={user_detail.name} 
        profilePhoto={user_detail.profilePhoto} 
        company={user_detail.companyName} 
        branch={user_detail.branchName}
        status={user_detail.status}
        roles={user_detail.roles}
        accountStatus={user_detail.accountStatus}
        onClickChangeRole={handleUserDetailClickChangeRole}
        onDelete={handleUserDetailClickDelete}
      />
      <Menu
        triggerSubMenuAction="click"
        onClick={(event: any) => history.push(event.key)}
        selectedKeys={[location.pathname]}
        mode="horizontal"
        className='ml-3'
        >
        {filterRoute().map((route: IRoute) => (
          <Menu.Item key={route.path.replace(':userId', params.userId)}>{t(route.meta.attrs?.label || '')}</Menu.Item>
        ))}
      </Menu>
    </div>

    {/* Body */}
    <div className='back-office-user-detail-card-body'>
        {children}
    </div>

    {/* Footer */}
    <div>
      {/* Button */}
      {
        user_detail.status === USER_STATUS.WAITING_APPROVAL ? 
        <div className='flex flex-row justify-end my-4 pb-4 lg:pb-0 space-x-2'>
          <Button
            type="primary"
            className={`custom-button`}
            onClick={handleUserDetailClickRejectButton}
            loading={user_loading.isDetailLoading || user_loading.isLoading || user_loading.isActionLoading}
            danger
            >
              {t('user.action.rejectRegistration')}
          </Button>
          <Button
            type="primary"
            className={`custom-button`}
            onClick={handleUserDetailClickApproveButton}
            loading={user_loading.isDetailLoading || user_loading.isLoading || user_loading.isActionLoading}
            >
              {t('user.action.approveRegistration')}
          </Button>
        </div> : user_detail.status === USER_STATUS.APPROVED ?
        <div className='flex flex-row justify-end my-4 pb-4'>
          <Button
            type="primary"
            className={`custom-button`}
            onClick={handleUserDetailClickChangeAccountStatus}
            loading={user_loading.isDetailLoading || user_loading.isLoading || user_loading.isActionLoading}
            style={{
              backgroundColor: user_detail.accountStatus ? '#FF5858' : '#0E38B7',
              borderColor: user_detail.accountStatus ? '#FF5858' : '#0E38B7'
            }}
            >
              { user_detail.accountStatus ? t('user.action.deActivate') : t('user.action.activate')}
          </Button>
        </div> : <></>
      }

      {/* User - Approve Registration Dialog */}
      <UserApproveRegistrationDialog
        isOpen={dialog.isApproveDialogOpen}
        loading={user_loading.isDetailLoading || masterRole_loading.isDropdownLoading}
        onClickFirstButton={handleUserApproveClickFirstButton}
        onClickCloseButton={() => handleDialog('isApproveDialogOpen', false)}
        attrs={{
          isRoleLoading: masterRole_loading.isDropdownLoading,
          roleDropdown: masterRole_dropdown,
          isUserActionLoading: user_loading.isActionLoading,
        }}
      />

      {/* User - Reject Registration Dialog */}
      <UserRejectRegistrationDialog
        isOpen={dialog.isRejectDialogOpen}
        loading={user_loading.isDetailLoading}
        onClickFirstButton={handleUserRejectClickFirstButton}
        onClickCloseButton={() => handleDialog('isRejectDialogOpen', false)}
        attrs={{
          isUserActionLoading: user_loading.isActionLoading,
        }}
      />

      {/* User - Change Role Dialog */}
      <ChangeRoleDialog
        isOpen={dialog.isChangeRoleDialogOpen}
        loading={user_loading.isDetailLoading || masterRole_loading.isDropdownLoading}
        onClickFirstButton={changeUserRole}
        onClickCloseButton={() => handleDialog('isChangeRoleDialogOpen', false)}
        attrs={{
          roles: user_detail.roles,
          isRoleLoading: masterRole_loading.isDropdownLoading,
          roleDropdown: masterRole_dropdown,
          isUserActionLoading: user_loading.isActionLoading,
        }}
      />

      {/* User - Confirmation When Approve / Reject / Delete  */}
      <BaseDialogVerification
        isOpen={dialog.isConfirmationDialogOpen}
        body={renderDialogVerificationBody()}
        firstButtonLabel="app.sure"
        secondButtonLabel="app.no"
        onFirstButtonClick={() => handleVerificationClickFirstButton()}
        onSecondButtonClick={() => handleDialog('isConfirmationDialogOpen', false)}
        onButtonCloseClick={() => handleDialog('isConfirmationDialogOpen', false)}
        isFirstButtonLoading={user_loading.isDetailLoading || user_loading.isActionLoading}
        isUsingSecondButton={!user_loading.isDetailLoading}
        isHorizontalFooter
        isUsingDefaultFooter={detailDialogEditableType !== USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS}
        footer={renderDialogVerificationFooter()}
        closable
      />
    </div>
  </>
})

export { BackOfficeUserDetailCard }